// hooks/useEventWizardApi.js
import { useCallback, useMemo } from 'react';
import * as S from '../services/eventWizardApi';

export function useEventWizardApi(backendUrl) {
  const listInstances = useCallback(async () => {
    const r = await S.ewList(backendUrl);
    if (!r.data?.success) throw new Error(r.data?.message || 'List failed');
    return r.data.instances;
  }, [backendUrl]);

  const launch       = useCallback((eventName, cams, audios, videoInstanceType, audioInstanceType, controllerChannel, region) =>
    S.ewLaunch(backendUrl, { eventName, cams: +cams, audios: +audios, videoInstanceType, audioInstanceType, controllerChannel, region }), [backendUrl]
  );
  const launchMaster = useCallback((eventName, region, controllerChannel) =>
    S.ewLaunchMaster(backendUrl, eventName, region, controllerChannel), [backendUrl]
  );
  const terminate    = useCallback((instanceId) =>
    S.ewTerminate(backendUrl, instanceId), [backendUrl]
  );

  const getEvent     = useCallback((evt) => S.ewGetEvent(backendUrl, evt), [backendUrl]);
  const deleteEvent  = useCallback((evt) => S.ewDeleteEvent(backendUrl, evt), [backendUrl]);
  const saveEventCmd = useCallback((evt, payload) => S.ewSaveEventCmd(backendUrl, evt, payload), [backendUrl]);
  const globalCmds   = useCallback(() => S.ewGlobalCommands(backendUrl), [backendUrl]);

  const saveMpd      = useCallback((evt, mpd) => S.ewSaveMpd(backendUrl, evt, mpd), [backendUrl]);
  const saveRoom     = useCallback((evt, roomKey) => S.ewSaveRoom(backendUrl, evt, roomKey), [backendUrl]);

  const checkRtmp    = useCallback((ip) => S.ewCheckRtmp(backendUrl, ip), [backendUrl]);

  const updateNodeScript = useCallback(
    (instanceId, command) => S.ewUpdateNodeScript(backendUrl, instanceId, command),
    [backendUrl]
  );

  // Manifest vía proxy al master
  const getManifest  = useCallback(
    (instanceId, eventName) => S.ewGetManifest(backendUrl, instanceId, eventName),
    [backendUrl]
  );
  const setManifest  = useCallback(
    (instanceId, mpd, eventName, mpd8K, mpdFull) => S.ewSetManifest(backendUrl, instanceId, mpd, eventName, mpd8K, mpdFull),
    [backendUrl]
  );

  // Perfiles de cámara + rebuild MPD
  const saveCamProfile = useCallback(
    (evt, payload) => S.ewSaveCamProfile(backendUrl, evt, payload),
    [backendUrl]
  );
  const rebuildMpd = useCallback(
    (evt, camMpdData, masterInstanceId, opts) => S.ewRebuildMpd(backendUrl, evt, camMpdData, masterInstanceId, opts),
    [backendUrl]
  );
    const addMpdFragment = useCallback(
    (evt, payload) => S.ewAddMpdFragment(backendUrl, evt, payload),
    [backendUrl]
  );

  const removeMpdFragment = useCallback(
    (evt, instanceId) => S.ewRemoveMpdFragment(backendUrl, evt, instanceId),
    [backendUrl]
  );

  const toggleMpdFragment = useCallback(
    (evt, camName, role, enabled) => S.ewToggleMpdFragment(backendUrl, evt, camName, role, enabled),
    [backendUrl]
  );

  // Every function above is already useCallback([backendUrl]); only this wrapper
  // object was new on each render, and consumers list `api` in their effect deps.
  return useMemo(() => ({
    listInstances,
    launch, launchMaster, terminate,
    getEvent, deleteEvent, saveEventCmd, globalCmds,
    saveMpd, saveRoom, saveDashSettings: (evt, dashSettings) => S.ewSaveDashSettings(backendUrl, evt, dashSettings),
    checkRtmp,
    updateNodeScript,
    getManifest, setManifest,
    listPresets: S.ewListPresets.bind(null, backendUrl),
    createPreset: (payload) => S.ewCreatePreset(backendUrl, payload),
    updatePreset: (id, payload) => S.ewUpdatePreset(backendUrl, id, payload),
    deletePreset: (id) => S.ewDeletePreset(backendUrl, id),
    setDefaultPreset: (id) => S.ewSetDefaultPreset(backendUrl, id),
    // nuevos:
    saveCamProfile,
    rebuildMpd,
	addMpdFragment,
    removeMpdFragment,
    toggleMpdFragment,
    setTrackOrder: (evt, orders) => S.ewSetTrackOrder(backendUrl, evt, orders),
    getCamLabels: (evt) => S.ewGetCamLabels(backendUrl, evt),
    saveCamLabel: (evt, instanceId, label) => S.ewSaveCamLabel(backendUrl, evt, instanceId, label),
    getContentOffsets: (evt) => S.ewGetContentOffsets(backendUrl, evt),
    saveContentOffset: (evt, camName, offsetSeconds) => S.ewSaveContentOffset(backendUrl, evt, camName, offsetSeconds),
    reanchorAst: (evt, body) => S.ewReanchorAst(backendUrl, evt, body),
    startFfmpeg: (instanceId, eventName, camName, extra) => S.ewStartFfmpeg(backendUrl, instanceId, eventName, camName, extra),
    stopFfmpeg: (instanceId) => S.ewStopFfmpeg(backendUrl, instanceId),
    archiveSession: (evt, archiveTs) => S.ewArchiveSession(backendUrl, evt, archiveTs),
    pointToVod: (evt, archiveTs, vodTimestamp) => S.ewPointToVod(backendUrl, evt, archiveTs, vodTimestamp),
    prepareFfmpeg: (instanceId, eventName, camName, archiveTs) => S.ewPrepareFfmpeg(backendUrl, instanceId, eventName, camName, archiveTs),
    goFfmpeg: (instanceId, body) => S.ewGoFfmpeg(backendUrl, instanceId, body),
    ffmpegReady: (instanceId) => S.ewFfmpegReady(backendUrl, instanceId),
    publishStart: (instanceId, eventName) => S.ewPublishStart(backendUrl, instanceId, eventName),
    publishStop: (instanceId) => S.ewPublishStop(backendUrl, instanceId),
    resetEventSession: (evt) => S.ewResetEventSession(backendUrl, evt),
    endEventSession: (evt) => S.ewEndEventSession(backendUrl, evt),
    generateVodMpd: (evt, masterInstanceId) => S.ewGenerateVodMpd(backendUrl, evt, masterInstanceId),
    // SRS Config & Controller Source
    getSrsConfig: (instanceId) => S.ewGetSrsConfig(backendUrl, instanceId),
    setSrsConfig: (instanceId, config) => S.ewSetSrsConfig(backendUrl, instanceId, config),
    getControllerSource: (instanceId) => S.ewGetControllerSource(backendUrl, instanceId),
    setControllerSource: (instanceId, source) => S.ewSetControllerSource(backendUrl, instanceId, source),
    applyAll: (payload) => S.ewApplyAll(backendUrl, payload),
    rebootInstances: (eventName, instanceId) => S.ewRebootInstances(backendUrl, eventName, instanceId),
    // Per-camera health/sync monitor (IR-198) — supersedes /drift. Slate,
    // failover and source-override calls are gone with the failover proxy.
    getMonitor: (instanceId) => S.ewGetMonitor(backendUrl, instanceId),
    setLowLatency: (evt, lowLatency) => S.ewSetLowLatency(backendUrl, evt, lowLatency),
    listElasticIps: () => S.ewListElasticIps(backendUrl),
    associateEip: (instanceId, allocationId) => S.ewAssociateEip(backendUrl, instanceId, allocationId),
    // Latency target (seconds; backend clamps up to the safe floor)
    setLiveDelay: (evt, liveDelay) => S.ewSetLiveDelay(backendUrl, evt, liveDelay),
    // Declared ingest profiles (Mongo, per event+camera — source of truth)
    getIngestProfiles: (evt) => S.ewGetIngestProfiles(backendUrl, evt),
    saveIngestProfile: (evt, camName, profile) => S.ewSaveIngestProfile(backendUrl, evt, camName, profile),
    saveNormalizerQuality: (evt, quality) => S.ewSaveNormalizerQuality(backendUrl, evt, quality),
    // Declared ingest config (per-instance copy on the controller)
    getIngestConfig: (instanceId) => S.ewGetIngestConfig(backendUrl, instanceId),
    setIngestConfig: (instanceId, config) => S.ewSetIngestConfig(backendUrl, instanceId, config),
    clearIngestConfig: (instanceId) => S.ewClearIngestConfig(backendUrl, instanceId),
    // LiveKit (WHIP) path, parallel to DASH
    setStreamingMode: (evt, mode) => S.ewSetStreamingMode(backendUrl, evt, mode),
    getLivekitIngress: (evt) => S.ewGetLivekitIngress(backendUrl, evt),
    getLivekitParticipants: (evt) => S.ewGetLivekitParticipants(backendUrl, evt),
    createLivekitTestToken: (evt) => S.ewCreateLivekitTestToken(backendUrl, evt),
    livekitStart: (instanceId) => S.ewLivekitStart(backendUrl, instanceId),
    livekitStop: (instanceId) => S.ewLivekitStop(backendUrl, instanceId),
    livekitStatus: (instanceId) => S.ewLivekitStatus(backendUrl, instanceId),
    createLivekitIngress: (evt, camName, participantName) => S.ewCreateLivekitIngress(backendUrl, evt, camName, participantName),
    deleteLivekitIngress: (evt, camName) => S.ewDeleteLivekitIngress(backendUrl, evt, camName),
    applyLivekitPreset: (evt, payload) => S.ewApplyLivekitPreset(backendUrl, evt, payload),
    // Listing each callback would just restate `backendUrl`: they are all
    // useCallback([backendUrl]), so they change exactly when it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [backendUrl]);
}


